import { useEffect, useState, useCallback } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { QRCodeSVG } from "qrcode.react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Coins, Check, Zap, Loader2, Sparkles, Smartphone, Copy, ShieldCheck, Receipt } from "lucide-react";

export default function Billing() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [upi, setUpi] = useState(null);
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const planParam = params.get("plan");
  const periodParam = params.get("period") === "annual" ? "annual" : "monthly";
  const [tab, setTab] = useState(planParam ? "subscription" : "credits");

  const load = useCallback(async () => {
    const [c, u] = await Promise.all([
      api.get("/billing/config"),
      api.get("/billing/upi/config").catch(() => ({ data: { enabled: false } })),
    ]);
    setConfig(c.data);
    setUpi(u.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!config) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar />
        <div className="py-32 text-center font-serif text-xl text-muted-foreground">Loading billing…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <main className="mx-auto max-w-5xl px-6 py-12">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-serif text-3xl font-medium tracking-tight">Billing &amp; credits</h1>
            <p className="mt-2 text-muted-foreground">
              Credits power document generation — one credit per document.
            </p>
          </div>
          <div className="flex items-center gap-3 rounded-sm border border-border bg-card px-5 py-4" data-testid="credit-balance">
            <Coins size={22} className="text-primary" />
            <div>
              <div className="font-serif text-2xl font-semibold leading-none">
                {config.unlimited ? "Unlimited" : config.balance}
              </div>
              <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                {config.unlimited ? "complimentary account" : `credits · ${config.subscription} plan`}
              </div>
            </div>
          </div>
        </div>

        {config.unlimited && (
          <div
            className="mt-6 rounded-sm border border-primary/30 bg-primary/5 px-5 py-4 text-sm text-muted-foreground"
            data-testid="unlimited-notice"
          >
            This account has <span className="font-medium text-foreground">unlimited credits</span> — every
            generation runs free, no purchase needed.
          </div>
        )}

        <div className="mt-8 flex rounded-sm border border-border bg-card p-1">
          {[
            ["credits", "Credit packs"],
            ["subscription", "Subscriptions"],
          ].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setTab(v)}
              data-testid={`billing-tab-${v}`}
              className={`flex-1 rounded-sm px-4 py-2 text-sm ${
                tab === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {tab === "credits" ? (
          <PacksSection config={config} upi={upi} onDone={load} />
        ) : (
          <PlansSection config={config} onDone={load} initialPeriod={periodParam} highlight={planParam} />
        )}

        <PaymentHistory key={config.balance} />

        {user?.role === "admin" && upi?.enabled && <UpiApprovals onApproved={load} />}
      </main>
    </div>
  );
}

/* ---------------- Credit packs (PayPal + UPI) ---------------- */
function PacksSection({ config, upi, onDone }) {
  const [method, setMethod] = useState("paypal");
  const upiOn = upi?.enabled;

  return (
    <div>
      <div className="mt-8 flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Pay with</span>
        <div className="flex rounded-sm border border-border bg-card p-1">
          <button
            onClick={() => setMethod("paypal")}
            data-testid="method-paypal"
            className={`rounded-sm px-3 py-1.5 text-sm ${method === "paypal" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
          >
            PayPal (USD)
          </button>
          {upiOn && (
            <button
              onClick={() => setMethod("upi")}
              data-testid="method-upi"
              className={`rounded-sm px-3 py-1.5 text-sm ${method === "upi" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            >
              UPI (INR)
            </button>
          )}
        </div>
      </div>
      {method === "paypal" ? (
        <>
          <div className="mt-4 rounded-sm border border-primary/40 bg-primary/5 px-4 py-3 text-sm text-primary">
            Live mode — real payments will be charged to your PayPal account.
          </div>
          <PayPalPacks config={config} onDone={onDone} />
        </>
      ) : (
        <UpiPacks upi={upi} onDone={onDone} />
      )}
    </div>
  );
}

function PayPalPacks({ config, onDone }) {
  return (
    <PayPalScriptProvider
      options={{ clientId: config.client_id, currency: config.currency, intent: "capture", dataNamespace: "paypal_packs" }}
    >
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="credit-packs">
        {config.packs.map((pack) => (
          <div key={pack.id} className="flex flex-col rounded-sm border border-border bg-card p-6">
            <Zap size={20} className="text-primary" />
            <h3 className="mt-3 font-serif text-xl font-semibold">{pack.name}</h3>
            <div className="mt-1 font-serif text-3xl font-semibold">
              {pack.credits}
              <span className="ml-1 text-sm font-normal text-muted-foreground">credits</span>
            </div>
            <div className="mt-1 text-muted-foreground">${pack.price}</div>
            <div className="mt-6" data-testid={`buy-pack-${pack.id}`}>
              <PayPalButtons
                style={{ layout: "vertical", color: "black", shape: "rect", label: "pay", height: 40 }}
                forceReRender={[pack.id]}
                createOrder={async () => {
                  const { data } = await api.post("/billing/orders", { pack_id: pack.id });
                  return data.id;
                }}
                onApprove={async (data) => {
                  try {
                    const { data: res } = await api.post(`/billing/orders/${data.orderID}/capture`);
                    toast.success(`Added ${res.credits_added ?? pack.credits} credits`);
                    onDone();
                  } catch (err) {
                    toast.error(formatApiErrorDetail(err.response?.data?.detail));
                  }
                }}
                onError={() => toast.error("PayPal payment could not be completed")}
              />
            </div>
          </div>
        ))}
      </div>
    </PayPalScriptProvider>
  );
}

function UpiPacks({ upi, onDone }) {
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(null);
  const [utr, setUtr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const start = async (pack) => {
    setBusy(pack.id);
    try {
      const { data } = await api.post("/billing/upi/order", { pack_id: pack.id });
      setOrder({ ...data, packName: pack.name });
      setUtr("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const submit = async () => {
    if (utr.trim().length < 6) {
      toast.error("Enter the UPI reference / UTR from your payment app");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/billing/upi/submit", { payment_id: order.payment_id, utr: utr.trim() });
      toast.success("Submitted — credits will appear once approved");
      setOrder(null);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const copyVpa = () => {
    navigator.clipboard?.writeText(upi.vpa);
    toast.success("UPI ID copied");
  };

  return (
    <div>
      <div className="mt-4 rounded-sm border border-border bg-secondary/40 px-4 py-3 text-sm text-muted-foreground">
        Pay to <span className="font-medium text-foreground">{upi.vpa}</span> via any UPI app, then enter your
        transaction reference. Credits are added after admin approval (usually quickly).
      </div>
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="upi-packs">
        {upi.packs.map((pack) => (
          <div key={pack.id} className="flex flex-col rounded-sm border border-border bg-card p-6">
            <Smartphone size={20} className="text-primary" />
            <h3 className="mt-3 font-serif text-xl font-semibold">{pack.name}</h3>
            <div className="mt-1 font-serif text-3xl font-semibold">
              {pack.credits}
              <span className="ml-1 text-sm font-normal text-muted-foreground">credits</span>
            </div>
            <div className="mt-1 text-muted-foreground">₹{pack.inr}</div>
            <Button
              onClick={() => start(pack)}
              disabled={busy === pack.id}
              className="mt-6 rounded-sm"
              data-testid={`upi-buy-${pack.id}`}
            >
              {busy === pack.id ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Pay with UPI
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={!!order} onOpenChange={(o) => !o && setOrder(null)}>
        <DialogContent className="sm:max-w-md" data-testid="upi-pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Pay ₹{order?.amount_inr} via UPI</DialogTitle>
            <DialogDescription>
              Scan the QR with any UPI app, or tap the button on mobile. Then paste your UTR below.
            </DialogDescription>
          </DialogHeader>
          {order && (
            <div className="space-y-4">
              <div className="flex justify-center rounded-sm border border-border bg-white p-4">
                <QRCodeSVG value={order.upi_link} size={190} data-testid="upi-qr" />
              </div>
              <button
                onClick={copyVpa}
                className="flex w-full items-center justify-center gap-2 rounded-sm border border-border py-2 text-sm"
                data-testid="upi-copy-vpa"
              >
                <Copy size={14} /> {order.vpa}
              </button>
              <a href={order.upi_link} data-testid="upi-open-app">
                <Button variant="outline" className="w-full rounded-sm">
                  <Smartphone size={16} className="mr-2" /> Open UPI app
                </Button>
              </a>
              <div className="space-y-2">
                <Input
                  value={utr}
                  onChange={(e) => setUtr(e.target.value)}
                  placeholder="UPI transaction ID / UTR (12 digits)"
                  className="rounded-sm"
                  data-testid="upi-utr-input"
                />
                <Button onClick={submit} disabled={submitting} className="w-full rounded-sm" data-testid="upi-submit">
                  {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                  I've paid — submit for approval
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Subscriptions (PayPal) ---------------- */
function PlansSection({ config, onDone, initialPeriod = "monthly", highlight }) {
  const [period, setPeriod] = useState(initialPeriod === "annual" ? "annual" : "monthly");
  const [canceling, setCanceling] = useState(false);
  const annualMonths = config.annual_months || 10;

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? Your remaining credits stay in your account, but your monthly refill will stop.")) return;
    setCanceling(true);
    try {
      await api.post("/billing/subscriptions/cancel");
      toast.success("Subscription canceled — your remaining credits are still yours.");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCanceling(false);
    }
  };

  const missingPlans = config.plans.some((p) => !p.plan_id_monthly || !p.plan_id_annual);
  if (config.plans_error || missingPlans) {
    return (
      <div className="mt-8 rounded-sm border border-dashed border-border py-16 text-center text-muted-foreground" data-testid="plans-preparing">
        Subscription plans are being prepared. Please refresh in a moment.
      </div>
    );
  }

  const currentTier = config.plans.find((p) => p.key === config.subscription);

  return (
    <div>
      {/* Active subscription banner */}
      {config.subscription_active && currentTier && (
        <div
          className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-sm border border-[hsl(var(--matcha))] bg-[hsl(var(--matcha)/0.07)] px-5 py-4"
          data-testid="active-subscription-banner"
        >
          <div className="flex items-center gap-3">
            <Check size={18} className="text-[hsl(var(--matcha))]" />
            <div>
              <div className="font-serif text-lg font-semibold">
                You're on the {currentTier.name} plan
                <span className="ml-2 font-mono text-xs uppercase tracking-wider text-muted-foreground">{config.subscription_period}</span>
              </div>
              <div className="text-sm text-muted-foreground">
                {currentTier.credits.toLocaleString()} credits refill
                {config.subscription_next_refill ? ` on ${new Date(config.subscription_next_refill).toLocaleDateString()}` : " monthly"}.
                Pick another plan below to switch.
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            className="rounded-sm text-destructive"
            onClick={cancel}
            disabled={canceling}
            data-testid="cancel-subscription-button"
          >
            {canceling ? <Loader2 size={15} className="mr-2 animate-spin" /> : null}
            Cancel subscription
          </Button>
        </div>
      )}

      {/* Billing period toggle */}
      <div className="mt-8 inline-flex items-center gap-1 rounded-sm border border-border bg-card p-1" data-testid="sub-billing-toggle">
        {[
          ["monthly", "Monthly"],
          ["annual", "Annual"],
        ].map(([v, l]) => (
          <button
            key={v}
            onClick={() => setPeriod(v)}
            data-testid={`sub-toggle-${v}`}
            className={`flex items-center gap-2 rounded-sm px-4 py-1.5 font-mono text-xs uppercase tracking-wider transition-colors ${
              period === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {l}
            {v === "annual" && (
              <span className={`rounded-sm px-1.5 py-0.5 text-[10px] ${period === "annual" ? "bg-primary-foreground/20" : "bg-[hsl(var(--matcha)/0.18)] text-[hsl(var(--matcha))]"}`}>
                2 months free
              </span>
            )}
          </button>
        ))}
      </div>

      <PayPalScriptProvider
        options={{ clientId: config.client_id, currency: config.currency, intent: "subscription", vault: true, dataNamespace: "paypal_subs" }}
      >
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="subscription-plans">
          {config.plans.map((plan) => {
            const active = config.subscription_active && config.subscription === plan.key;
            const isHighlight = highlight === plan.key;
            const planId = period === "annual" ? plan.plan_id_annual : plan.plan_id_monthly;
            const planKey = `${plan.key}_${period}`;
            const price = period === "annual" ? plan.annual_price : plan.monthly_price;
            const perMonth = period === "annual" ? (plan.annual_price / 12).toFixed(2) : plan.monthly_price;
            return (
              <div
                key={plan.key}
                data-testid={`sub-card-${plan.key}`}
                className={`flex flex-col rounded-sm border p-6 ${
                  active
                    ? "border-[hsl(var(--matcha))] bg-[hsl(var(--matcha)/0.06)]"
                    : isHighlight
                    ? "border-[hsl(var(--teal))] shadow-[0_0_0_1px_hsl(var(--teal))]"
                    : "border-border bg-card"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Sparkles size={18} className="text-[hsl(var(--teal))]" />
                  <h3 className="font-serif text-xl font-semibold">{plan.name}</h3>
                  {active && (
                    <span className="ml-auto flex items-center gap-1 text-xs uppercase tracking-wider text-[hsl(var(--matcha))]">
                      <Check size={14} /> Current
                    </span>
                  )}
                </div>
                <div className="mt-3 font-serif text-3xl font-semibold">
                  ${period === "annual" ? perMonth : price}
                  <span className="ml-1 text-sm font-normal text-muted-foreground">/ mo</span>
                </div>
                {period === "annual" && (
                  <div className="mt-1 font-mono text-[11px] text-[hsl(var(--teal))]">billed ${price}/yr</div>
                )}
                <p className="mt-2 text-sm text-muted-foreground">
                  {plan.credits.toLocaleString()} generation credits / month.
                </p>
                <div className="mt-6" data-testid={`subscribe-${plan.key}`}>
                  {active ? (
                    <div className="rounded-sm border border-dashed border-[hsl(var(--matcha))]/50 py-3 text-center text-xs font-mono uppercase tracking-wider text-[hsl(var(--matcha))]">
                      Active plan
                    </div>
                  ) : (
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "rect", label: "subscribe", height: 40 }}
                      forceReRender={[planKey, planId]}
                      createSubscription={(data, actions) => actions.subscription.create({ plan_id: planId })}
                      onApprove={async (data) => {
                        try {
                          const { data: res } = await api.post("/billing/subscriptions/activate", {
                            subscription_id: data.subscriptionID,
                            plan_key: planKey,
                          });
                          toast.success(`${plan.name} activated — ${res.credits_added} credits added`);
                          onDone();
                        } catch (err) {
                          toast.error(formatApiErrorDetail(err.response?.data?.detail));
                        }
                      }}
                      onError={() => toast.error("PayPal subscription could not be completed")}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </PayPalScriptProvider>
    </div>
  );
}

/* ---------------- Payment history ---------------- */
function PaymentHistory() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    const fetchHistory = () =>
      api.get("/billing/history").then((r) => setRows(r.data)).catch(() => setRows([]));
    fetchHistory();
    const t = setInterval(fetchHistory, 10000);
    return () => clearInterval(t);
  }, []);

  const providerLabel = { paypal: "PayPal", upi: "UPI" };
  const statusStyle = (s) => {
    const v = (s || "").toLowerCase();
    if (v === "completed" || v === "approved") return "text-[hsl(150_40%_35%)]";
    if (v === "submitted" || v === "created") return "text-[hsl(30_60%_42%)]";
    if (v === "rejected") return "text-destructive";
    return "text-muted-foreground";
  };
  const statusLabel = (s) => {
    const v = (s || "").toLowerCase();
    if (v === "completed") return "Completed";
    if (v === "approved") return "Approved";
    if (v === "submitted") return "Pending approval";
    if (v === "created") return "Awaiting payment";
    if (v === "rejected") return "Rejected";
    return s;
  };
  const money = (r) =>
    r.currency === "INR" ? `₹${r.amount}` : r.currency === "USD" ? `$${r.amount}` : `${r.amount} ${r.currency}`;

  return (
    <div className="mt-12 rounded-sm border border-border bg-card" data-testid="payment-history">
      <div className="flex items-center gap-2 border-b border-border px-6 py-4">
        <Receipt size={18} className="text-primary" />
        <h2 className="font-serif text-lg font-semibold">Payment history</h2>
      </div>
      {rows === null ? (
        <div className="px-6 py-10 text-center text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="px-6 py-10 text-center text-muted-foreground" data-testid="history-empty">
          No purchases yet.
        </div>
      ) : (
        <div className="divide-y divide-border">
          {rows.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-4 px-6 py-3.5 text-sm" data-testid="history-row">
              <span className="w-16 rounded-sm bg-secondary px-2 py-0.5 text-center text-xs">
                {providerLabel[r.provider] || r.provider}
              </span>
              <span className="flex-1 capitalize">
                {r.type === "subscription" ? "Subscription" : "Credit pack"} · +{r.credits} credits
              </span>
              <span className="text-muted-foreground">{money(r)}</span>
              {r.utr && <span className="font-mono text-xs text-muted-foreground">UTR {r.utr}</span>}
              <span className={`w-32 text-right font-medium ${statusStyle(r.status)}`}>
                {statusLabel(r.status)}
                {(r.status || "").toLowerCase() === "rejected" && (
                  <span className="block text-[10px] font-normal text-muted-foreground">
                    You can submit again
                  </span>
                )}
              </span>
              <span className="w-40 text-right text-xs text-muted-foreground">
                {r.date ? new Date(r.date).toLocaleString() : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Admin: UPI approvals ---------------- */
function UpiApprovals({ onApproved }) {
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/billing/upi/admin/pending");
    setPending(data);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/billing/upi/admin/${id}/${action}`);
      toast.success(action === "approve" ? "Approved & credited" : "Rejected");
      await load();
      onApproved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? pending.filter(
        (p) =>
          (p.user_email || "").toLowerCase().includes(q) ||
          String(p.amount_inr).includes(q) ||
          (p.utr || "").toLowerCase().includes(q)
      )
    : pending;

  return (
    <div className="mt-12 rounded-sm border border-border bg-card" data-testid="upi-approvals">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-6 py-4">
        <ShieldCheck size={18} className="text-primary" />
        <h2 className="font-serif text-lg font-semibold">UPI approvals</h2>
        <span className="ml-1 rounded-sm bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
          {pending.length} pending
        </span>
        <div className="ml-auto w-full sm:w-64">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search email, amount or UTR…"
            className="h-8 rounded-sm text-sm"
            data-testid="approvals-search"
          />
        </div>
      </div>
      {pending.length === 0 ? (
        <div className="px-6 py-10 text-center text-muted-foreground">No UPI payments awaiting approval.</div>
      ) : filtered.length === 0 ? (
        <div className="px-6 py-10 text-center text-muted-foreground" data-testid="approvals-no-match">
          No approvals match “{query}”.
        </div>
      ) : (
        <div>
          {filtered.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-4 border-b border-border px-6 py-4 last:border-0">
              <div className="min-w-0 flex-1">
                <div className="font-medium">{p.user_email}</div>
                <div className="text-sm text-muted-foreground">
                  ₹{p.amount_inr} · {p.credits} credits · UTR {p.utr}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-sm text-destructive"
                  onClick={() => act(p.id, "reject")}
                  disabled={busy === p.id}
                  data-testid={`upi-reject-${p.id}`}
                >
                  Reject
                </Button>
                <Button
                  size="sm"
                  className="rounded-sm"
                  onClick={() => act(p.id, "approve")}
                  disabled={busy === p.id}
                  data-testid={`upi-approve-${p.id}`}
                >
                  {busy === p.id ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
                  Approve
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
